import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Select from '@/components/common/Select';
import EmptyState from '@/components/common/EmptyState';
import { supabase } from '@/services/supabase';
import { formatTimeAgo } from '@/utils/formatters';

interface AppIncident {
  id: string;
  fingerprint: string;
  flow: string;
  step: string;
  status: 'open' | 'resolved';
  severity: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
  environment: string;
  platform: string | null;
  release_first_seen: string | null;
  release_last_seen: string | null;
  error_code: string | null;
  error_type: string | null;
  error_message: string | null;
  event_count: number;
  affected_user_count: number;
  affected_business_count: number;
  first_seen_at: string;
  last_seen_at: string;
  last_alert_sent_at: string | null;
  resolved_at: string | null;
}

interface AppObservabilityEvent {
  id: string;
  incident_id: string | null;
  flow: string;
  step: string;
  status: 'started' | 'success' | 'failure';
  severity: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
  environment: string;
  platform: string | null;
  release: string | null;
  route: string | null;
  user_id: string | null;
  business_id: string | null;
  error_code: string | null;
  error_type: string | null;
  error_message: string | null;
  correlation_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const severityStyles: Record<AppIncident['severity'], string> = {
  debug: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-100 text-blue-700',
  warning: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
  fatal: 'bg-rose-100 text-rose-800',
};

const statusStyles: Record<AppIncident['status'], string> = {
  open: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
};

async function fetchIncidents() {
  const { data, error } = await supabase
    .from('app_incidents')
    .select('*')
    .order('status', { ascending: true })
    .order('last_seen_at', { ascending: false })
    .limit(200);

  if (error) throw error;
  return (data || []) as AppIncident[];
}

async function fetchRecentEvents() {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('app_observability_events')
    .select(
      'id, incident_id, flow, step, status, severity, environment, platform, release, route, user_id, business_id, error_code, error_type, error_message, correlation_id, metadata, created_at'
    )
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(1000);

  if (error) throw error;
  return (data || []) as AppObservabilityEvent[];
}

async function updateIncidentStatus(id: string, status: AppIncident['status']) {
  const { error } = await supabase
    .from('app_incidents')
    .update({
      status,
      resolved_at: status === 'resolved' ? new Date().toISOString() : null,
    })
    .eq('id', id);

  if (error) throw error;
}

function buildFlowHealth(events: AppObservabilityEvent[]) {
  const totals = new Map<string, { flow: string; success: number; failure: number }>();

  events.forEach((event) => {
    if (event.status !== 'success' && event.status !== 'failure') return;
    const existing = totals.get(event.flow) || { flow: event.flow, success: 0, failure: 0 };
    if (event.status === 'success') {
      existing.success += 1;
    } else {
      existing.failure += 1;
    }
    totals.set(event.flow, existing);
  });

  return Array.from(totals.values())
    .map((row) => {
      const total = row.success + row.failure;
      return {
        ...row,
        total,
        successRate: total > 0 ? Math.round((row.success / total) * 1000) / 10 : 100,
      };
    })
    .sort((left, right) => {
      if (left.failure !== right.failure) return right.failure - left.failure;
      return left.successRate - right.successRate;
    });
}

export default function Operations() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | AppIncident['status']>('open');
  const [flowFilter, setFlowFilter] = useState('all');

  const { data: incidents = [], isLoading: incidentsLoading } = useQuery({
    queryKey: ['operations', 'incidents'],
    queryFn: fetchIncidents,
    refetchInterval: 30000,
  });

  const { data: recentEvents = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['operations', 'events-24h'],
    queryFn: fetchRecentEvents,
    refetchInterval: 30000,
  });

  const incidentStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AppIncident['status'] }) =>
      updateIncidentStatus(id, status),
    onSuccess: () => {
      toast.success('Incident status updated');
      queryClient.invalidateQueries({ queryKey: ['operations'] });
    },
    onError: () => {
      toast.error('Failed to update incident status');
    },
  });

  const flowOptions = useMemo(() => {
    const flows = Array.from(new Set(incidents.map((incident) => incident.flow))).sort();
    return [
      { value: 'all', label: 'All Flows' },
      ...flows.map((flow) => ({ value: flow, label: flow })),
    ];
  }, [incidents]);

  const filteredIncidents = useMemo(() => {
    return incidents.filter((incident) => {
      if (statusFilter !== 'all' && incident.status !== statusFilter) return false;
      if (flowFilter !== 'all' && incident.flow !== flowFilter) return false;
      return true;
    });
  }, [flowFilter, incidents, statusFilter]);

  const failedEvents = useMemo(
    () => recentEvents.filter((event) => event.status === 'failure'),
    [recentEvents]
  );

  const flowHealth = useMemo(() => buildFlowHealth(recentEvents), [recentEvents]);
  const affectedUsers24h = useMemo(
    () => new Set(failedEvents.map((event) => event.user_id).filter(Boolean)).size,
    [failedEvents]
  );
  const successEvents24h = useMemo(
    () => recentEvents.filter((event) => event.status === 'success').length,
    [recentEvents]
  );
  const successRate24h = useMemo(() => {
    const total = successEvents24h + failedEvents.length;
    if (total === 0) return 100;
    return Math.round((successEvents24h / total) * 1000) / 10;
  }, [failedEvents.length, successEvents24h]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Operations</h1>
        <p className="mt-1 text-sm text-gray-500">
          Production incident triage and recent client flow telemetry
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Open Incidents</p>
              <p className="text-2xl font-bold text-gray-900">
                {incidents.filter((incident) => incident.status === 'open').length}
              </p>
            </div>
            <ExclamationTriangleIcon className="w-10 h-10 text-red-500" />
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">24h Failures</p>
              <p className="text-2xl font-bold text-gray-900">{failedEvents.length}</p>
            </div>
            <ClockIcon className="w-10 h-10 text-yellow-500" />
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Affected Users 24h</p>
              <p className="text-2xl font-bold text-gray-900">{affectedUsers24h}</p>
            </div>
            <SignalIcon className="w-10 h-10 text-blue-500" />
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">24h Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{successRate24h}%</p>
            </div>
            <CheckCircleIcon className="w-10 h-10 text-green-500" />
          </CardBody>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={statusFilter}
          onChange={(nextValue) => setStatusFilter(String(nextValue) as typeof statusFilter)}
          options={[
            { value: 'open', label: 'Open Incidents' },
            { value: 'resolved', label: 'Resolved Incidents' },
            { value: 'all', label: 'All Incidents' },
          ]}
          aria-label="Incident status filter"
        />
        <Select
          value={flowFilter}
          onChange={(nextValue) => setFlowFilter(String(nextValue))}
          options={flowOptions}
          aria-label="Incident flow filter"
        />
      </div>

      <Card>
        <CardBody>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Incidents</h2>
          {incidentsLoading ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredIncidents.length === 0 ? (
            <EmptyState title="No incidents" description="No incidents match the current filters." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flow / Step
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Error
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Release / Platform
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Seen
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredIncidents.map((incident) => (
                    <tr key={incident.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="text-sm font-semibold text-gray-900">{incident.flow}</p>
                        <p className="text-xs text-gray-500">{incident.step}</p>
                        <p className="text-[11px] text-gray-400 mt-1 break-all">{incident.fingerprint}</p>
                      </td>
                      <td className="px-4 py-4 space-y-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            statusStyles[incident.status]
                          }`}
                        >
                          {incident.status}
                        </span>
                        <div>
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              severityStyles[incident.severity]
                            }`}
                          >
                            {incident.severity}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {incident.error_code || 'unknown_error'}
                        </p>
                        <p className="text-xs text-gray-500 max-w-md truncate">
                          {incident.error_message || incident.error_type || 'No error message'}
                        </p>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        <p>{incident.release_last_seen || incident.release_first_seen || 'unknown'}</p>
                        <p className="text-xs text-gray-500">
                          {incident.platform || 'unknown'} / {incident.environment}
                        </p>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        <p>{incident.event_count} events</p>
                        <p className="text-xs text-gray-500">
                          {incident.affected_user_count} users / {incident.affected_business_count} businesses
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Last {formatTimeAgo(incident.last_seen_at)}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            incidentStatusMutation.mutate({
                              id: incident.id,
                              status: incident.status === 'open' ? 'resolved' : 'open',
                            })
                          }
                        >
                          {incident.status === 'open' ? 'Resolve' : 'Reopen'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardBody>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Flows Last 24h</h2>
            {flowHealth.length === 0 ? (
              <EmptyState title="No flow telemetry" description="No success/failure events have been captured in the last 24 hours." />
            ) : (
              <div className="space-y-3">
                {flowHealth.slice(0, 12).map((row) => (
                  <div key={row.flow} className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{row.flow}</p>
                      <p className="text-xs text-gray-500">
                        {row.success} success / {row.failure} failure
                      </p>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{row.successRate}%</span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Failures</h2>
            {eventsLoading ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              </div>
            ) : failedEvents.length === 0 ? (
              <EmptyState title="No recent failures" description="No failures were captured in the last 24 hours." />
            ) : (
              <div className="space-y-3">
                {failedEvents.slice(0, 20).map((event) => (
                  <div key={event.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {event.flow}/{event.step}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {event.error_code || 'unknown_error'} · {event.error_message || event.error_type || 'No error message'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {event.route || 'unknown route'} · {event.platform || 'unknown platform'} · {event.release || 'unknown release'}
                        </p>
                        {event.correlation_id && (
                          <p className="text-[11px] text-gray-400 mt-1 break-all">
                            {event.correlation_id}
                          </p>
                        )}
                      </div>
                      <span
                        className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          severityStyles[event.severity]
                        }`}
                      >
                        {event.severity}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">{formatTimeAgo(event.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
